import { IoClose } from '@react-icons/all-files/io5/IoClose'
import { Flex, IconButton, Popover, Text } from '@radix-ui/themes'
import React from 'react'

type PopoverContentProps = React.ComponentProps<typeof Popover.Content>

type Props = {
  title: string
  content: React.ReactNode
  children: React.ReactNode
  className?: string
  side?: PopoverContentProps['side']
  align?: PopoverContentProps['align']
  sideOffset?: PopoverContentProps['sideOffset']
  width?: PopoverContentProps['width']
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

/**
 * Popover with a title header and built-in close (X) button.
 *
 * Wraps `@radix-ui/themes` Popover. Pass the trigger as children; `title` renders
 * in the header alongside a close IconButton, and `content` renders as the body.
 *
 * @see https://www.radix-ui.com/themes/docs/components/popover
 */
export const PopoverWithClose = ({
  title,
  content,
  children,
  className,
  side,
  align,
  sideOffset,
  width,
  open,
  onOpenChange,
}: Props) => {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content
        className={className}
        side={side}
        align={align}
        sideOffset={sideOffset}
        width={width}
      >
        <Flex align="center" justify="between" gap="2" mb="3">
          <Text size="2" weight="bold">
            {title}
          </Text>
          <Popover.Close>
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              aria-label="Close"
            >
              <IoClose />
            </IconButton>
          </Popover.Close>
        </Flex>
        {content}
      </Popover.Content>
    </Popover.Root>
  )
}
